import {
  formatCourt, LIBELLE_MOTIF_RELANCE, LIBELLE_MOYEN_RELANCE,
  type MotifRelance, type MoyenRelanceJournal,
} from '@mosaik/core';
import type { Litige } from '../api/types';

/**
 * Colonne « Litige » : les relances déjà faites, une icône par geste.
 *
 * **Elle ne garde que le passé.** Elle mêlait autrefois deux natures : des
 * relances passées et des problèmes EN COURS — livraison bloquée, envoi en
 * échec, commande introuvable. Ces trois-là lèvent déjà une action dans
 * « À faire », où se lit ce qui reste à faire ; les répéter ici brouillait la
 * lecture des deux colonnes.
 *
 * **Une relance, une icône.** L'agrégat « 2 relances » sous une icône chiffrée
 * ne disait ni quand ni pourquoi.
 *
 * **L'icône dit CE QU'ON RÉCLAMAIT**, pas par quel tuyau on l'a réclamé. Le
 * moyen — mail, téléphone, courrier — reste consigné à l'historique, parce
 * qu'il atteste une relance faite hors de l'outil, mais il ne se lit pas d'un
 * coup d'œil dans une liste : savoir qu'une commande a été relancée deux fois
 * ne dit rien tant qu'on ignore si c'était pour une date d'expédition, pour une
 * commande que le fournisseur n'a jamais enregistrée, pour un numéro de suivi
 * jamais communiqué, ou pour une livraison qui traîne. Ce sont quatre histoires
 * différentes, et c'est cela qu'on veut voir.
 *
 * Les relances encore sans réponse gardent leur couleur ; celles dont on a
 * obtenu ce qu'on réclamait s'estompent — la trace demeure, l'attente non.
 */

/** Calendrier : on réclamait une date d'expédition. */
const IconeDateExpedition = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
    <rect x="3" y="5" width="18" height="16" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 9.5h18M8 3.2v3.4M16 3.2v3.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M7.5 13.5h3v3h-3z" fill="currentColor" />
  </svg>
);

/** Carton barré : la commande est introuvable chez le fournisseur. */
const IconeCommandeIntrouvable = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
    <path
      d="M3.5 7.5l8.5-4 8.5 4v9l-8.5 4-8.5-4z"
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"
    />
    <path d="M3.5 7.5l8.5 4 8.5-4M12 11.5v9" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5.5 5l13 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/** Camion : on réclamait la livraison. */
const IconeLivraison = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
    <path
      d="M1.8 6h12v9.5h-12zM13.8 9.5h4l2.7 3v3h-6.7z"
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"
    />
    <circle cx="6.8" cy="17.6" r="1.9" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="16.8" cy="17.6" r="1.9" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

/** Étiquette code-barres : on réclamait le numéro de suivi. */
const IconeTracking = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
    <rect x="2.5" y="5" width="19" height="14" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M6 8.5v7M9 8.5v7M12 8.5v7M15.5 8.5v7M18 8.5v7"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
    />
  </svg>
);

const ICONES: Record<MotifRelance, () => JSX.Element> = {
  relance: IconeDateExpedition,
  probleme: IconeCommandeIntrouvable,
  tracking: IconeTracking,
  livraison: IconeLivraison,
};

/**
 * Une commande que le fournisseur n'a jamais enregistrée n'est pas un retard :
 * c'est une commande perdue, qui se rejoue. La brique la distingue du simple
 * retard d'expédition, que l'ocre suffit à signaler.
 */
const COULEURS: Record<MotifRelance, string> = {
  relance: 'var(--ocre)',
  probleme: 'var(--brick)',
  // Un numéro qui tarde est une attente ordinaire, pas une commande perdue :
  // la marchandise roule, il manque de quoi la suivre.
  tracking: 'var(--ocre)',
  livraison: 'var(--ocre)',
};

/**
 * Un litige sous délai se distingue d'un litige échu.
 *
 * Les deux sont « en cours » — ce qu'on réclamait n'est pas arrivé —, mais ils
 * n'appellent pas le même regard : le premier attend une réponse qu'on a le
 * droit d'attendre, le second a dépassé son terme et réclame de nouveau. Le
 * cercle pointillé dit « la balle est dans leur camp ».
 */
function styleDelai(l: Litige): React.CSSProperties {
  if (!l.encours) return { opacity: 0.55 };
  return l.dansLesDelais
    ? { outline: '1px dashed currentColor', outlineOffset: 2, borderRadius: '50%' }
    : {};
}

export function Litiges({ litiges }: { litiges: Litige[] }) {
  if (!litiges.length) return <span style={{ color: 'var(--ink-3)' }}>—</span>;

  return (
    <span style={{ display: 'inline-flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
      {litiges.map((l, i) => {
        const motif: MotifRelance = l.motif in ICONES ? l.motif : 'relance';
        const Icone = ICONES[motif];
        return (
          <span
            // Plusieurs relances peuvent partager motif, cible et date : le rang
            // est le seul identifiant sûr, et la liste ne se réordonne pas.
            key={`${l.cle}-${motif}-${l.le ?? ''}-${i}`}
            title={l.titre}
            aria-label={l.titre || `Relancé pour ${LIBELLE_MOTIF_RELANCE[motif]}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: l.encours ? COULEURS[motif] : 'var(--ink-3)',
              ...styleDelai(l),
            }}
          >
            <Icone />
          </span>
        );
      })}
    </span>
  );
}

/** Comment la relance s'est faite, dit en clair. */
function libelleMoyen(moyen: MoyenRelanceJournal): string {
  if (moyen === 'mail') return "par mail depuis l'outil";
  if (moyen === 'inconnu') return 'moyen non consigné';
  return LIBELLE_MOYEN_RELANCE[moyen];
}

/** Où en est ce qu'on réclamait : obtenu, attendu, ou en retard. */
function Statut({ litige: l }: { litige: Litige }) {
  if (!l.encours) return <span className="tag">Obtenu</span>;
  if (l.dansLesDelais && l.echeance) {
    return <span className="tag ocre">Réponse attendue le {formatCourt(l.echeance)}</span>;
  }
  return (
    <span className="tag brick">
      {l.echeance ? `Sans réponse depuis le ${formatCourt(l.echeance)}` : 'Sans réponse'}
    </span>
  );
}

/**
 * Encart « Litiges » de la fiche : le détail de ce que la colonne ne montre
 * qu'en icônes — quand, auprès de qui, comment, pour quoi, et où l'on en est.
 * Rien tant que la commande n'a connu aucun litige.
 */
export function EncartLitiges({ litiges }: { litiges: Litige[] }) {
  if (!litiges.length) return null;
  const enCours = litiges.filter((l) => l.encours).length;

  return (
    <div className="fieldset">
      <h3 style={{ marginBottom: 10 }}>
        Litiges — {enCours ? `${enCours} en cours sur ${litiges.length}` : 'tous résolus'}
      </h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {litiges.map((l, i) => {
          const motif: MotifRelance = l.motif in ICONES ? l.motif : 'relance';
          const Icone = ICONES[motif];
          return (
            <li
              key={`${l.cle}-${motif}-${l.le ?? ''}-${i}`}
              style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--line)' }}
            >
              <span style={{ color: l.encours ? COULEURS[motif] : 'var(--ink-3)', paddingTop: 2 }}>
                <Icone />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 600 }}>Relance pour {LIBELLE_MOTIF_RELANCE[motif]}</span>
                  <Statut litige={l} />
                </div>
                <div className="sub">
                  {l.le ? `Le ${formatCourt(l.le)}` : 'Date non consignée'}, auprès de {l.aupres},{' '}
                  {libelleMoyen(l.moyen)}.
                  {l.detail && <> <span style={{ overflowWrap: 'anywhere' }}>{l.detail}</span></>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
